import { useState, useCallback } from 'react';
import { useLocation } from 'wouter';
import { AnimatePresence } from 'framer-motion';
import { Radio, CheckCircle } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { EmptyState } from '@/components/shared/EmptyState';
import { NowCard } from '@/components/live/NowCard';
import { NextCard } from '@/components/live/NextCard';
import { CountdownTimer } from '@/components/live/CountdownTimer';
import { RunningBehindBanner } from '@/components/live/RunningBehindBanner';
import { UpcomingPreview } from '@/components/live/UpcomingPreview';
import { useInitWedding } from '@/hooks/useWedding';
import { useEvents, useUpdateEventStatus } from '@/hooks/useEvents';
import { useLiveState } from '@/hooks/useLiveState';
import { useCountdown } from '@/hooks/useCountdown';
import { useRunningBehind } from '@/hooks/useRunningBehind';
import type { EventStatus } from '@/lib/types';

export function LivePage() {
  const [, navigate] = useLocation();
  const { weddingId } = useInitWedding();
  const { data: events = [] } = useEvents(weddingId, { refetchInterval: 5000 });
  const updateStatus = useUpdateEventStatus(weddingId ?? '');
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const handleAutoTransition = useCallback(
    (eventId: string, status: EventStatus) => {
      updateStatus.mutate({ eventId, status });
    },
    [updateStatus]
  );

  const liveState = useLiveState(events, handleAutoTransition);
  const countdown = useCountdown(liveState.countdownTarget);
  const { isBehind, driftMinutes } = useRunningBehind(events);

  const showBanner = isBehind && !bannerDismissed;

  // Events beyond NEXT for UpcomingPreview (up to 3)
  const nextEventId = liveState.nextEvent?.id;
  const upcomingPreviewEvents = events
    .filter(
      (e) =>
        e.status === 'upcoming' &&
        e.id !== nextEventId &&
        new Date(e.startTime).getTime() > (liveState.nextEvent
          ? new Date(liveState.nextEvent.startTime).getTime()
          : Date.now())
    )
    .slice(0, 3);

  const countdownLabel = liveState.nextEvent
    ? `until ${liveState.nextEvent.name}`
    : liveState.nowEvent
    ? `until ${liveState.nowEvent.name} ends`
    : 'until next event';

  // Empty state
  if (events.length === 0) {
    return (
      <div
        className="flex flex-col min-h-screen"
        style={{ background: 'var(--color-background)' }}
      >
        <Header title="Live" />
        <main className="flex-1 pb-20">
          <EmptyState
            icon={Radio}
            title="No events in timeline"
            description="Add events to your schedule to see the live dashboard here."
            action={
              <button
                className="px-6 py-3 rounded-xl text-sm font-semibold touch-target"
                style={{ background: 'var(--color-primary)', color: '#1A1A2E' }}
                onClick={() => navigate('/schedule')}
              >
                Go to Schedule
              </button>
            }
          />
        </main>
      </div>
    );
  }

  // All complete
  if (liveState.allComplete) {
    const completedCount = events.filter(
      (e) => e.status === 'completed' || e.status === 'skipped'
    ).length;
    return (
      <div
        className="flex flex-col min-h-screen"
        style={{ background: 'var(--color-background)' }}
      >
        <Header title="Live" />
        <main className="flex-1 flex flex-col items-center justify-center pb-20 px-8 text-center gap-4">
          <div
            className="rounded-2xl p-5"
            style={{ background: 'var(--color-primary-muted)' }}
          >
            <CheckCircle size={48} style={{ color: 'var(--color-primary)' }} />
          </div>
          <div className="space-y-1">
            <h2
              className="font-bold"
              style={{ fontSize: '22px', color: 'var(--color-text)' }}
            >
              Timeline Complete
            </h2>
            <p style={{ fontSize: '15px', color: 'var(--color-text-secondary)' }}>
              All {completedCount} events finished. Great work today.
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Live dashboard
  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ background: 'var(--color-background)' }}
    >
      <Header title="Live" />

      <AnimatePresence>
        {showBanner && (
          <RunningBehindBanner
            driftMinutes={driftMinutes}
            onDismiss={() => setBannerDismissed(true)}
          />
        )}
      </AnimatePresence>

      <main className="flex-1 pb-24 overflow-y-auto">
        <div className="mt-4">
          <NowCard
            event={liveState.nowEvent}
            progressPercent={liveState.progressPercent}
            isRunningOver={liveState.isRunningOver}
            timeRemaining={countdown.formatted}
          />
        </div>

        <div className="mt-3">
          <NextCard
            event={liveState.nextEvent}
            minutesUntil={liveState.minutesUntilNext}
          />
        </div>

        {countdown.formatted && (
          <div className="mt-6">
            <CountdownTimer
              formatted={countdown.formatted}
              label={countdownLabel}
            />
          </div>
        )}

        <UpcomingPreview events={upcomingPreviewEvents} />
      </main>
    </div>
  );
}
